import { t } from 'i18next';
import { WeekDays } from 'models/general';
import moment from 'moment-timezone';

export const humanizeSeconds = (secondsInput?: number): string => {
  if (!secondsInput) {
    return '--';
  }

  const duration = moment.duration({ seconds: secondsInput });
  const seconds = duration.seconds();
  const minutes = duration.minutes();
  const hours = duration.hours();
  let output = '';

  if (hours) {
    output += `${hours}${t('common.reductions.h')}`;
  }

  if (minutes) {
    if (output) {
      output += ' ';
    }

    output += `${minutes}${t('common.reductions.m')}`;
  }

  if (seconds) {
    output += ' ' + `${seconds}${t('common.reductions.s')}`;
  } else if (!output) {
    output += `${seconds}${t('common.reductions.s')}`;
  }

  return output;
};

export function humanizeSecondsWithoutDays(secondsInput?: number): string {
  if (!secondsInput) {
    return '--';
  }

  const totalSeconds = Math.floor(secondsInput);
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}${t('common.reductions.h')} ${minutes % 60}${t('common.reductions.m')}`;
  }

  if (minutes > 0) {
    return `${minutes}${t('common.reductions.m')}`;
  }

  return `${totalSeconds}${t('common.reductions.s')}`;
}

export type TimeRange = { from: string; to: string };

export const fullDayTimeRange: TimeRange = { from: '00:00', to: '23:59' } as const;

/**
 *
 * @param from date to start checking for weekdays
 * @param to date to provide availabile weekends up to it
 * @param options
 *  allWeekdaysOnMissingValue: if no from or to wether to provide all available weekdays or nothing
 * @returns
 */
export const getWeekdays = (
  from?: moment.Moment | null,
  to?: moment.Moment | null,
  options?: { allWeekdaysOnMissingValue?: boolean; timezone?: string },
): WeekDays[] => {
  if (!from || !to) {
    return options?.allWeekdaysOnMissingValue ? (Array.from({ length: 7 }, (_, index: number) => index) as WeekDays[]) : [];
  }

  // Form values from MDatePicker are wall-clock-in-tz: the digits the user picked are meaningful
  // IN options.timezone. Use keepLocalTime so the day-of-week is read off those digits instead of
  // the underlying instant — otherwise the result drifts by ±1 day relative to the displayed date
  // when the moment's current .tz() differs from options.timezone. When no timezone is provided
  // (e.g. multi-driver create where the picker tz collapses to undefined), read weekdays off the
  // moment's INTERNAL tz, which is site tz via moment.tz.setDefault — NOT browser-local. See
  // utils/timezone/index.ts header for the convention.
  const start = options?.timezone ? from.clone().tz(options.timezone, true).startOf('day') : from.clone().startOf('day');
  const end = options?.timezone ? to.clone().tz(options.timezone, true).startOf('day') : to.clone().startOf('day');

  const weekdays: WeekDays[] = [];

  while (start.isSameOrBefore(end, 'day') && weekdays.length < 7) {
    const weekday = start.get('weekday') === 6 ? 0 : ((start.get('weekday') + 1) as WeekDays);

    if (!weekdays.includes(weekday)) {
      weekdays.push(weekday);
    }

    start.add(1, 'day');
  }

  return weekdays;
};

type TimeOfDayLike = { hour: number; minute: number };

export const timeOfDayToMinutes = (time?: TimeOfDayLike | null): number | null => (time ? time.hour * 60 + time.minute : null);

/** Formats a wall-clock time-of-day as zero-padded HH:mm (empty string when absent). */
export const formatTimeOfDay = (time?: TimeOfDayLike | null): string =>
  time ? `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}` : '';

export const timeStringToObject = (time: string | null, key: string): { [key: string]: { hour: number; minute: number } } => {
  if (!time) {
    return {};
  }

  const [hour, minute] = time.split(':').map((value) => Number(value));

  return {
    [key]: {
      hour,
      minute,
    },
  };
};
